import type { HittingBayAvailabilityEntry, HittingBaySlot } from '@/types/booking.types';

// ─── ISO → HH:MM Local Time ───────────────────────────────────────────────────

/**
 * Convert an ISO UTC string to a 24-hour HH:MM string in the given timezone.
 * Normalises any "24:xx" Intl output to "00:xx".
 */
export function isoToLocalHHMM(iso: string, timezone: string): string {
  const formatted = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(new Date(iso));

  // Intl can return "24:00" at midnight in some environments; normalise to "00:00"
  if (formatted.startsWith('24:')) {
    return '00:' + formatted.slice(3);
  }
  return formatted;
}

// ─── End-time derivation helper ───────────────────────────────────────────────

/**
 * Derive end times for a sorted list of HH:MM start times.
 * Hitting bay slots are always 60 minutes — endTime = startTime + 60 min.
 *
 * Using next-slot-start as endTime breaks when there is a gap in availability
 * (e.g. Bay 1 has 07:00 and 09:00 but not 08:00): 07:00 would get endTime 09:00,
 * making 09:00 appear as a consecutive adjacent candidate and showing the wrong
 * time range on the confirmation screen.
 */
function deriveEndTimes(startTimes: string[]): string[] {
  return startTimes.map((startTime) => {
    const [h, m] = startTime.split(':').map(Number);
    const totalMin = h * 60 + m + 60;
    const endH = Math.floor(totalMin / 60) % 24;
    const endM = totalMin % 60;
    return String(endH).padStart(2, '0') + ':' + String(endM).padStart(2, '0');
  });
}

// ─── buildSlotUnion ───────────────────────────────────────────────────────────

/**
 * Build a priority-ordered union of hitting bay slots for a given date.
 *
 * Entries must be passed in priority-ascending order (API guarantees this).
 * First entry encountering a startTime is the highest-priority bay (primary calendarId).
 * Subsequent entries with the same startTime add their calendarId to alternateCalendarIds.
 *
 * EndTime for each slot is startTime + 60 min (hitting bay slots are always 60 min).
 */
export function buildSlotUnion(
  entries: HittingBayAvailabilityEntry[],
  dateKey: string,
  timezone: string
): HittingBaySlot[] {
  const slotMap = new Map<string, HittingBaySlot>();

  for (const entry of entries) {
    const isoSlots = entry.availability[dateKey] ?? [];
    for (const isoSlot of isoSlots) {
      const startTime = isoToLocalHHMM(isoSlot, timezone);
      if (!startTime.endsWith(':00')) continue; // hitting bay slots are 60-min, on the hour only
      if (!slotMap.has(startTime)) {
        slotMap.set(startTime, {
          startTime,
          endTime: '', // filled below
          calendarId: entry.calendarId,
          alternateCalendarIds: [],
        });
      } else {
        slotMap.get(startTime)!.alternateCalendarIds.push(entry.calendarId);
      }
    }
  }

  if (slotMap.size === 0) return [];

  // Sort by startTime
  const slots = Array.from(slotMap.values()).sort((a, b) =>
    a.startTime.localeCompare(b.startTime)
  );

  // Fill endTimes
  const endTimes = deriveEndTimes(slots.map((s) => s.startTime));
  slots.forEach((s, i) => {
    s.endTime = endTimes[i];
  });

  return slots;
}

// ─── buildManualBaySlots ──────────────────────────────────────────────────────

/**
 * Build the slot list for a manually selected bay.
 * Returns only the selected bay's slots for the given date,
 * each with alternateCalendarIds: [] (no alternates in manual mode).
 */
export function buildManualBaySlots(
  entries: HittingBayAvailabilityEntry[],
  dateKey: string,
  timezone: string,
  selectedCalendarId: string
): HittingBaySlot[] {
  const entry = entries.find((e) => e.calendarId === selectedCalendarId);
  if (!entry) return [];

  const isoSlots = entry.availability[dateKey] ?? [];
  if (isoSlots.length === 0) return [];

  const startTimes = isoSlots
    .map((iso) => isoToLocalHHMM(iso, timezone))
    .filter((t) => t.endsWith(':00')); // hitting bay slots are 60-min, on the hour only
  const sorted = [...startTimes].sort((a, b) => a.localeCompare(b));
  const endTimes = deriveEndTimes(sorted);

  return sorted.map((startTime, i) => ({
    startTime,
    endTime: endTimes[i],
    calendarId: entry.calendarId,
    alternateCalendarIds: [],
  }));
}
